import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import clsx from 'clsx';

export interface AllianceStatusItem {
  value: string;
  label: string;
  tone?: 'default' | 'primary' | 'danger';
}

interface Props {
  items: AllianceStatusItem[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function AllianceApplicationStatusMenu({ items, value, onChange, disabled }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentLabel = items.find((item) => item.value === value)?.label ?? '';

  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 300;
      setMenuRect(rect);
      const bottomSpace = window.innerHeight - rect.bottom;
      const topSpace = rect.top;
      setMenuPosition(bottomSpace < menuHeight && topSpace > menuHeight ? 'top' : 'bottom');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => closeMenu();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, closeMenu]);

  // Escape でメニューを閉じ、フォーカスをトリガーへ戻す（キーボード操作対応）
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closeMenu();
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeMenu]);

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={clsx(
          'flex h-[42px] w-full appearance-none items-center rounded border border-solid border-border bg-white px-3',
          disabled && 'cursor-not-allowed opacity-60'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
      >
        <p className="truncate text-sm">{currentLabel}</p>
        {isOpen ? (
          <MdKeyboardArrowUp className="ml-auto size-5 shrink-0" />
        ) : (
          <MdKeyboardArrowDown className="ml-auto size-5 shrink-0" />
        )}
      </button>
      {isOpen &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[9999] w-[200px] rounded bg-white shadow-[0px_0px_12px_0px_#00000033]"
            style={{
              left: `${menuRect.left}px`,
              top: menuPosition === 'bottom' ? `${menuRect.bottom + 4}px` : undefined,
              bottom:
                menuPosition === 'top' ? `${window.innerHeight - menuRect.top + 4}px` : undefined,
            }}
          >
            {items.map((item) => (
              <button
                key={item.value}
                type="button"
                role="menuitem"
                className={clsx(
                  'flex h-xxl w-full cursor-pointer appearance-none items-center border-none bg-transparent px-3 text-left text-sm hover:opacity-60',
                  item.tone === 'primary' && 'text-primary',
                  item.tone === 'danger' && 'text-danger'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.value !== value) onChange(item.value);
                  closeMenu();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
